<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="alert.isDiff"
    :isOutdated="isOutdated"
    :itemType="'ALERT'"
    :typename="'estero_alert'"
    :item="alert"
    :onUpdate="updateAlert"
    :onCreate="createAlert"
    :onDelete="deleteAlert"
    formClass=""
    @change-chosen="onChangeChosen"
    @revert="onRevert"
  >
    <fieldset slot="fieldset" :disabled="isOutdated">
      <div class="ui basic segment">
        <div class="ui info message" v-if="alert.target.user || alert.target.machine">
          <div
            v-html="$t('ALERT_TARGETS', { user: alert.target.user, machine: alert.target.machine })"
          ></div>
        </div>

        <form class="ui form">
          <h3 class="header capitalize-fl">{{ $tc('ALERT_RULE_TARGET') }}</h3>
          <div class="two fields" v-if="alert.target">
            <!-- User -->
            <div class="field required" :class="{ error: $v.alert.target.user.$error }">
              <label for="user">{{ $tc('USER', 1) }}</label>
              <input
                id="user"
                type="text"
                :placeholder="$tc('USER', 1)"
                v-model.trim="alert.target.user"
              />
            </div>
            <!-- Machine -->
            <div class="field required" :class="{ error: $v.alert.target.machine.$error }">
              <label for="user">{{ $tc('MACHINE', 1) }}</label>
              <input
                id="machine"
                type="text"
                :placeholder="$tc('MACHINE', 1)"
                v-model.trim="alert.target.machine"
              />
            </div>
          </div>
        </form>
      </div>
      <alert-rule :rule="alert" :inList="false" @delete="onAlertDelete" @change="onAlertChange" />
    </fieldset>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import { Application, Toast, ErrorCodes, UnsavedChecker, EsteroAlert } from 'services'
import { MainSection } from 'ui'
import { take } from 'rxjs/operators'
import { required } from 'vuelidate/lib/validators'
import AlertRule from './AlertRule'

const defaultAlert = {
  target: {}
}

export default {
  name: 'alert',
  mixins: [UnsavedChecker.mixin],
  components: { MainSection, AlertRule },
  data() {
    return {
      mode: '',
      alert: { ...defaultAlert },
      isOutdated: false,
      alertDiff: {}
    }
  },

  validations: {
    alert: {
      target: {
        user: required,
        machine: required
      }
    }
  },

  computed: {
    isEditMode() {
      return this.mode === 'edit'
    }
  },

  methods: {
    onAlertChange(alert) {
      this.alert = alert
    },

    onAlertDelete() {
      this.mode = 'create'
      this.alert = {}
    },

    readAlert(id: string) {
      return EsteroAlert.read(id)
        .then(alert => {
          this.alert = alert
          this.isOutdated = false
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('ALERT', 1) })
            })
          )
          console.error(err)
        })
    },

    createAlert() {
      if (!this.$v.$invalid) {
        return EsteroAlert.create(this.alert)
          .then(res => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('ALERT', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'alert',
              params: { alertId: res.id }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('ALERT', 1) })
              })
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('ALERT', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    updateAlert() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return EsteroAlert.update(this.alert.id, this.alert)
            .then(res => {
              this.alert.version = res.version
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('ALERT', 1) }), {
                remove: 5000
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', {
                      thing: this.$i18n.tc('ALERT', 1)
                    }),
                    thing: this.$i18n.tc('ALERT', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', {
                      thing: this.$i18n.tc('ALERT', 1)
                    })
                  }),
                  { remove: 5000 }
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('ALERT', 1) }),
              thing: this.$i18n.tc('ALERT', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('ALERT', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteAlert() {
      EsteroAlert.remove(this.alert.id)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('ALERT', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'alerts'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('ALERT', 1) })
            })
          )
          console.error(err)
        })
    },
    editMode() {
      this.mode = 'edit'
      this.readAlert(this.$route.params.alertId)
    },

    createMode() {
      this.mode = 'create'
      this.alert = { ...defaultAlert }
    },

    onChangeChosen({ changedItem, fullDiff }) {
      this.alert = changedItem
      this.alertDiff = fullDiff
    },

    onRevert(a) {
      this.alert = a
      this.alertDiff = {}
    }
  },

  mounted() {
    if (this.$route.params.alertId) {
      this.editMode()
    } else {
      this.createMode()
    }
  },

  watch: {
    '$route.params.alertId': function(newId) {
      this.$v.$reset()
      if (newId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
